import { inject, type App, type InjectionKey } from 'vue';
import { HttpClient } from '../http/HttpClient.ts';
import { AuthApi } from './AuthApi.ts';
import { HealthApi } from './HealthApi.ts';
import { OrgsApi } from './OrgsApi.ts';
import { ProjectsApi } from './ProjectsApi.ts';
import { WorkApi } from './WorkApi.ts';
import { SprintsApi } from './SprintsApi.ts';
import { CollabApi } from './CollabApi.ts';
import { DescriptionsApi } from './DescriptionsApi.ts';
import { DocsApi } from './DocsApi.ts';
import { LibraryApi } from './LibraryApi.ts';
import { NotificationsApi } from './NotificationsApi.ts';
import { AgentsApi } from './AgentsApi.ts';
import { AgentContextApi } from './AgentContextApi.ts';
import { AgentWorkApi } from './AgentWorkApi.ts';
import { AgentMessagingApi } from './AgentMessagingApi.ts';
import { BoardsApi } from './BoardsApi.ts';
import { SecurityApi } from './SecurityApi.ts';
import { PlansApi } from './PlansApi.ts';
import { DecisionsApi } from './DecisionsApi.ts';

export interface Api {
  health: HealthApi;
  auth: AuthApi;
  orgs: OrgsApi;
  projects: ProjectsApi;
  work: WorkApi;
  sprints: SprintsApi;
  collab: CollabApi;
  descriptions: DescriptionsApi;
  docs: DocsApi;
  library: LibraryApi;
  notifications: NotificationsApi;
  agents: AgentsApi;
  agentContext: AgentContextApi;
  agentWork: AgentWorkApi;
  agentMessaging: AgentMessagingApi;
  boards: BoardsApi;
  security: SecurityApi;
  plans: PlansApi;
  decisions: DecisionsApi;
}

export const API_KEY: InjectionKey<Api> = Symbol('dometry-api');

/** Builds all resource clients on one HttpClient and installs them as a Vue plugin. */
export function createApi({ http = new HttpClient() }: { http?: HttpClient } = {}): Api & {
  http: HttpClient;
  install(app: App): void;
} {
  const api: Api = {
    health: new HealthApi(http),
    auth: new AuthApi(http),
    orgs: new OrgsApi(http),
    projects: new ProjectsApi(http),
    work: new WorkApi(http),
    sprints: new SprintsApi(http),
    collab: new CollabApi(http),
    descriptions: new DescriptionsApi(http),
    docs: new DocsApi(http),
    library: new LibraryApi(http),
    notifications: new NotificationsApi(http),
    agents: new AgentsApi(http),
    agentContext: new AgentContextApi(http),
    agentWork: new AgentWorkApi(http),
    agentMessaging: new AgentMessagingApi(http),
    boards: new BoardsApi(http),
    security: new SecurityApi(http),
    plans: new PlansApi(http),
    decisions: new DecisionsApi(http),
  };
  return {
    ...api,
    http,
    install(app: App) {
      app.provide(API_KEY, api);
    },
  };
}

/** Access the API clients from a component or composable. */
export function useApi(): Api {
  const api = inject(API_KEY, null);
  if (!api) throw new Error('useApi() called without the API plugin installed');
  return api;
}
